import { useState, useCallback } from 'react';
import { User, AuthResponse, LoginRequest, RegisterRequest } from '@shared/types';
import { useUserStore } from '../stores/userStore';
import { API_BASE_URL, getAuthToken } from '../services/config';

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

async function makeAuthRequest(
  endpoint: string,
  data: unknown,
  method: 'POST' | 'GET' = 'POST'
): Promise<Response> {
  const token = getAuthToken();
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };

  if (method === 'POST' && data) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || 'Request failed');
  }

  return response;
}

// Auth actions on top of the persisted user store. The store is the single
// source of truth for user/token/isAuthenticated; this hook only adds the
// API calls and per-request loading/error state.
export function useAuth(): AuthState & AuthActions {
  const { user, token, isAuthenticated, setAuth, clearAuth } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await makeAuthRequest('/auth/login', credentials);
        const data: AuthResponse = await response.json();
        setAuth(data.user, data.token);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setAuth]
  );

  const register = useCallback(
    async (userData: RegisterRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await makeAuthRequest('/auth/register', userData);
        const data: AuthResponse = await response.json();
        setAuth(data.user, data.token);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Registration failed');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setAuth]
  );

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const refreshUser = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const response = await makeAuthRequest('/auth/me', null, 'GET');
      const freshUser: User = await response.json();
      useUserStore.getState().setUser(freshUser);
    } catch (err) {
      console.error('Failed to refresh user:', err);
      clearAuth();
    }
  }, [clearAuth]);

  const clearError = useCallback(() => setError(null), []);

  return {
    user,
    token,
    isAuthenticated,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };
}
